import Component from '@glimmer/component'
import { service } from '@ember/service'
import { array } from '@ember/helper'
import { LinkTo } from '@ember/routing'
import { t } from 'ember-intl'
import type SchoolRolloverService from 'teacher-ui-client/services/school-rollover'
import type SchoolRolloverLockService from 'teacher-ui-client/services/school-rollover-lock'
import type RouterService from '@ember/routing/router-service'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { RolloverState } from 'teacher-ui-client/school-rollover/manage-rollover-states'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type SessionService from 'teacher-ui-client/services/session'
import { InlineAlert, AlertType, InlineAlertSubtitle } from 'teacher-ui-client/components/inline-alert'

/**
 * Displays an alert when a page is locked due to rollover, indicating that some management features are disabled.
 * Different text shows depending on if they can continue the rollover or not.
 */
interface Signature {
  Element: HTMLDivElement
}

export class RolloverLockAlert extends Component<Signature> {
  @service schoolRollover!: SchoolRolloverService

  @service schoolRolloverLock!: SchoolRolloverLockService

  @service subscriptionType!: SubscriptionTypeService

  @service session!: SessionService

  @service router!: RouterService

  /**
   * Show locked alert if the specific management page is locked (due to an active rollover).
   * Also show for rollover page itself, which won't be locked in the normal way but we would like to show the message.
   */
  get showAlert() {
    return (
      this.schoolRolloverLock.isCurrentRouteLocked ||
      (this.router.currentRouteName?.startsWith('manage.rollover') && this.isRolloverLockedForSubType)
    )
  }

  private get isRolloverLockedForSubType() {
    switch (this.subscriptionType.current) {
      case SubscriptionType.Reading:
        return this.schoolRollover.readingRolloverState === RolloverState.Locked
      case SubscriptionType.Maths:
        return this.schoolRollover.mathsRolloverState === RolloverState.Locked
      case SubscriptionType.Writing:
        return this.schoolRollover.activeRollovers.length > 0
    }
    return false
  }

  get rolloverSubscriptionType(): SubscriptionType | undefined {
    return this.schoolRollover.activeRollovers[0]?.subscriptionType
  }

  /**
   * User can continue the rollover if they are the subco for the subscription-type of the rollover. This will affect
   * the messaging in the alert.
   */
  get userCanContinueRollover() {
    return this.rolloverSubscriptionType && this.session.currentTeacher.subcoFor(this.rolloverSubscriptionType)
  }

  <template>
    {{#if this.showAlert}}
      {{#if this.rolloverSubscriptionType}}
        <InlineAlert @type={{AlertType.Critical}} data-test-rollover-locked-alert ...attributes>
          <InlineAlertSubtitle class="space-y-4">
            <p>
              {{t "manage.rollover.lockAlert.featureDisabled" subscriptionType=(t this.rolloverSubscriptionType)}}
            </p>
            {{#if this.userCanContinueRollover}}
              <p>
                {{t "manage.rollover.lockAlert.youCanDoThis"}}
                <LinkTo @route="manage.rollover.wizard" @models={{array this.rolloverSubscriptionType}}>
                  {{t "manage.rollover.lockAlert.rolloverLink"}}
                </LinkTo>
              </p>
            {{else}}
              <p>
                {{t "manage.rollover.lockAlert.emailNotification" subscriptionType=(t this.rolloverSubscriptionType)}}
              </p>
            {{/if}}
          </InlineAlertSubtitle>
        </InlineAlert>
      {{/if}}
    {{/if}}
  </template>
}

export default RolloverLockAlert
